/**
 * Templates for docs and databases (REST /v1, routes.ts "Templates").
 *
 * - **Gallery**: built-in templates (projects & work, team & meetings, knowledge, personal, business), localized in
 *   en and pt-BR (names, descriptions *and* content follow the creator's language), plus saved templates:
 *   workspace templates (every member) and personal ones (only mine). "Use template" is `createPage` with
 *   `template: <ref>`; `startEmpty` leaves the sample rows out.
 * - **Saved templates** ("Save as template" on a page or database): a copy of the page subtree (bodies, databases
 *   with their schema, views and row templates, optionally rows) kept as hidden template content. Its id is the id of
 *   its root page: open that page to edit the template (`PageNode.templateId` / `GetPageResponse.template`).
 *   Personal templates: any member; workspace templates: admins, or members when `TemplateSettings` allows it.
 *   Guests and agents can't create templates.
 * - **Row templates** (docs.ts `RowTemplate`, listed on `Database.rowTemplates`): default cells plus a body for new
 *   rows; `CreateRowRequest.templateId` applies one, `UpdateDatabaseRequest.defaultRowTemplateId` picks the default.
 * - **Duplicate**: a copy of a page or database (and everything under it) right after it, named "Copy of …".
 *
 * Template and row-template changes are announced with the events that already exist (`database.updated` carries
 * row templates); the gallery list is fetched when the gallery opens.
 */
import { z } from "zod";
import { type BlockProps, type BlockType, PageVisibility, PropertyType, PropertyValue, RowTemplate, ViewLayout } from "./docs.ts";
import { Id, Locale, Timestamp } from "./entities.ts";

// ---------- Gallery ----------

export const TemplateCategory = z.enum(["projects", "meetings", "knowledge", "personal", "business"]);
export type TemplateCategory = z.infer<typeof TemplateCategory>;

/** builtin: ships with Bundle; workspace: saved for every member; personal: saved for me only. */
export const TemplateScope = z.enum(["builtin", "workspace", "personal"]);
export type TemplateScope = z.infer<typeof TemplateScope>;

/** One line of a page's body in a preview (headings, to-dos, callouts, toggles…). */
export const TemplateOutlineLine = z.object({
  type: z.string(),
  text: z.string(),
  /** to-dos */
  checked: z.boolean().optional(),
  /** callouts */
  icon: z.string().optional(),
  /** nesting under toggles / lists */
  depth: z.number().int().optional(),
});
export type TemplateOutlineLine = z.infer<typeof TemplateOutlineLine>;

export const TemplatePreviewDatabase = z.object({
  properties: z.array(z.object({ name: z.string(), type: PropertyType, options: z.array(z.string()).optional() })),
  views: z.array(z.object({ name: z.string(), layout: ViewLayout })),
  rowCount: z.number().int(),
  /** Titles of the first sample rows. */
  sampleRows: z.array(z.string()),
  /** Row templates offered by "New ▾". */
  rowTemplates: z.array(z.string()).optional(),
});
export type TemplatePreviewDatabase = z.infer<typeof TemplatePreviewDatabase>;

/** What "Use template" creates, flattened in tree order (`depth` 0 = created at the destination). */
export const TemplatePreviewNode = z.object({
  depth: z.number().int(),
  kind: z.enum(["page", "database"]),
  title: z.string(),
  icon: z.string().optional(),
  outline: z.array(TemplateOutlineLine).optional(),
  database: TemplatePreviewDatabase.optional(),
});
export type TemplatePreviewNode = z.infer<typeof TemplatePreviewNode>;

export const PageTemplateInfo = z.object({
  /** `CreatePageRequest.template`: a built-in id ("bug_tracker") or "custom:<id>". */
  ref: z.string(),
  /** Saved templates: the template id (= its root page id; open it to edit the template). */
  id: Id.optional(),
  scope: TemplateScope,
  category: TemplateCategory,
  name: z.string(),
  description: z.string(),
  icon: z.string(),
  /** What the first created node is. */
  kind: z.enum(["page", "database"]),
  /** Comes with sample rows ("Start empty" leaves them out). */
  hasSampleRows: z.boolean(),
  preview: z.array(TemplatePreviewNode),
  createdBy: Id.optional(),
  createdAt: Timestamp.optional(),
  updatedAt: Timestamp.optional(),
  /** I may rename, edit and delete it. */
  canManage: z.boolean(),
});
export type PageTemplateInfo = z.infer<typeof PageTemplateInfo>;

/** Who may save workspace templates (personal templates: every member; never guests or agents). */
export const TemplateSettings = z.object({ workspaceTemplateCreators: z.enum(["admins", "members"]) });
export type TemplateSettings = z.infer<typeof TemplateSettings>;
export const DEFAULT_TEMPLATE_SETTINGS: TemplateSettings = { workspaceTemplateCreators: "admins" };

export const ListTemplatesQuery = z.object({ locale: Locale.optional() });
export type ListTemplatesQuery = z.infer<typeof ListTemplatesQuery>;

/** Built-ins (in `locale`, default my profile language) then saved templates, newest first. */
export const ListTemplatesResponse = z.object({
  templates: z.array(PageTemplateInfo),
  canCreateWorkspace: z.boolean(),
  canCreatePersonal: z.boolean(),
  /** Admins may change `settings`. */
  canManageSettings: z.boolean(),
  settings: TemplateSettings,
});
export type ListTemplatesResponse = z.infer<typeof ListTemplatesResponse>;

export const TEMPLATE_NAME_MAX = 100;
export const TEMPLATE_DESCRIPTION_MAX = 500;

/** "Save as template": copies the page (or database) and everything under it. */
export const SaveAsTemplateRequest = z.object({
  name: z.string().min(1).max(TEMPLATE_NAME_MAX),
  description: z.string().max(TEMPLATE_DESCRIPTION_MAX).optional(),
  icon: z.string().max(64).optional(),
  category: TemplateCategory,
  scope: z.enum(["workspace", "personal"]),
  /** Copy database rows too (default: schema, views and row templates only). */
  includeRows: z.boolean().optional(),
  clientId: z.string().max(64).optional(),
});
export type SaveAsTemplateRequest = z.infer<typeof SaveAsTemplateRequest>;

export const UpdateTemplateRequest = z.object({
  name: z.string().min(1).max(TEMPLATE_NAME_MAX).optional(),
  description: z.string().max(TEMPLATE_DESCRIPTION_MAX).optional(),
  icon: z.string().max(64).optional(),
  category: TemplateCategory.optional(),
});
export type UpdateTemplateRequest = z.infer<typeof UpdateTemplateRequest>;

export const TemplateResponse = z.object({ template: PageTemplateInfo });
export type TemplateResponse = z.infer<typeof TemplateResponse>;

export const UpdateTemplateSettingsRequest = TemplateSettings.partial();
export type UpdateTemplateSettingsRequest = z.infer<typeof UpdateTemplateSettingsRequest>;
export const TemplateSettingsResponse = z.object({ settings: TemplateSettings });
export type TemplateSettingsResponse = z.infer<typeof TemplateSettingsResponse>;

// ---------- Duplicate ----------

/** Copies a page, database or row (and everything under it) right after it; responds like createPage. */
export const DuplicatePageRequest = z.object({
  /** Default: "Copy of <title>" in my language. */
  title: z.string().max(2000).optional(),
  /** Databases: copy their rows too (default true). */
  includeRows: z.boolean().optional(),
  /** Where the copy goes (default: next to the original, in its section). */
  parentId: Id.nullable().optional(),
  visibility: PageVisibility.optional(),
  locale: Locale.optional(),
  clientId: z.string().max(64).optional(),
});
export type DuplicatePageRequest = z.infer<typeof DuplicatePageRequest>;

// ---------- Row templates ----------

export const CreateRowTemplateRequest = z.object({
  name: z.string().max(2000),
  icon: z.string().max(64).optional(),
  values: z.record(Id, PropertyValue).optional(),
  /** Start from an existing row of the database: its cells (under `values`) and its body. */
  fromRowId: Id.optional(),
  /** Make it the database's default. */
  isDefault: z.boolean().optional(),
  clientId: z.string().max(64).optional(),
});
export type CreateRowTemplateRequest = z.infer<typeof CreateRowTemplateRequest>;

/** Partial: listed cells are replaced, `null` clears one. `afterId` reorders (null ⇒ first). */
export const UpdateRowTemplateRequest = z.object({
  name: z.string().max(2000).optional(),
  icon: z.string().max(64).optional(),
  values: z.record(Id, PropertyValue.nullable()).optional(),
  afterId: Id.nullable().optional(),
});
export type UpdateRowTemplateRequest = z.infer<typeof UpdateRowTemplateRequest>;

export const RowTemplateResponse = z.object({ template: RowTemplate });
export type RowTemplateResponse = z.infer<typeof RowTemplateResponse>;

// ---------- Template bodies (server-side; not on the wire) ----------

/** A Loro mark's value: true for bold/italic/…, a URL for links, an object for mentions ({ kind, id?, date? }). */
export type BodyMarkValue = boolean | string | number | Record<string, string | number | boolean>;

/** A run of rich text with Loro marks (bold, italic, link, mention… as in docs.ts). */
export type BodyRun = { text: string; marks?: Record<string, BodyMarkValue> };

/**
 * A page-body block as plain data: what built-in template definitions declare and what copies carry between page
 * bodies (PageDO builds a fresh Loro doc from it, model in docs.ts). `text` is plain text or marked runs.
 */
export type BodyBlock = {
  id?: string;
  type: BlockType;
  text?: string | BodyRun[];
  props?: BlockProps;
  children?: BodyBlock[];
};
